import toast from "react-hot-toast";
import {useIntl} from "react-intl";
import {type TaxiOrderRequest, useCreateTaxiOrder} from "@/api/apiClient";
import type {TaxiOverviewTierData} from "@/api/apiClient";
import {getOverviewLog, getPaymentMethod, logGtm} from "@/lib/gtm";

type CreateOrderResult = {
    orderId: string;
    customerName: string;
    phoneNumber: string;
};

/**
 * Hook for creating taxi orders
 * Handles order creation and GTM tracking
 */
export function useCreateOrder() {
    const intl = useIntl();
    const taxiOrder = useCreateTaxiOrder();

    const createOrder = async (
        payload: TaxiOrderRequest,
        tier?: TaxiOverviewTierData
    ): Promise<CreateOrderResult> => {
        const firstContact = payload.stops.at(0)?.contact;
        const customerName = firstContact?.name ?? "";
        const phoneNumber = firstContact?.phoneNumber ?? "";
        const paymentMethod = getPaymentMethod(payload.publicOrderPaymentType);
        try {
            const response = await taxiOrder.mutateAsync(payload);
            const orderId = response.data.orderId;

            // Log GTM event for all payment attempts
            logGtm.public.paymentAttempt({
                ...getOverviewLog(tier),
                paymentMethod,
                orderId,
            });

            return {
                orderId,
                customerName,
                phoneNumber,
            };
        } catch (error) {
            console.error("Order creation failed", error);
            toast.error(
                intl.formatMessage({
                    id: "kiosk_order_failed",
                    defaultMessage:
                        "The order failed. Please ask the staff for assistance or try again.",
                })
            );
            throw error;
        }
    };

    return {
        createOrder,
        isLoading: taxiOrder.isPending,
    };
}
